import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

/**
 * Scroll arrows only appear when tabs overflow the row.
 * Disabled edge buttons are fully removed from layout so they never flash on hover.
 */
const ProfileSectionTabs = ({ tabs, value, onChange }) => {
  const tabsRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  const updateOverflow = useCallback(() => {
    const root = tabsRef.current;
    if (!root) return;
    const scroller = root.querySelector('.MuiTabs-scroller');
    if (!scroller) return;
    // Subpixel rounding can make scrollWidth 1px larger than clientWidth.
    setNeedsScroll(scroller.scrollWidth > scroller.clientWidth + 1);
  }, []);

  useEffect(() => {
    updateOverflow();
    const root = tabsRef.current;
    if (!root) return undefined;

    const observer = new ResizeObserver(() => updateOverflow());
    observer.observe(root);
    const scroller = root.querySelector('.MuiTabs-scroller');
    if (scroller) observer.observe(scroller);

    window.addEventListener('resize', updateOverflow);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateOverflow);
    };
  }, [tabs, updateOverflow]);

  return (
    <Box mb={2} sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Tabs
        ref={tabsRef}
        value={value}
        onChange={(_event, next) => onChange(next)}
        variant='scrollable'
        scrollButtons={needsScroll}
        allowScrollButtonsMobile={needsScroll}
        sx={{
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            minHeight: 36,
            py: 0.5,
            fontWeight: 600,
          },
          '& .MuiTabs-scrollButtons.Mui-disabled': {
            display: 'none',
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.id} value={tab.id} label={tab.label} />
        ))}
      </Tabs>
    </Box>
  );
};

export default ProfileSectionTabs;
